import { useCallback, useState } from "react";
import { generateMarketingPage } from "@/network/network";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { showToast } from "@/other/utils/utils";

export function useMarketingPageGenerate() {
  const { selectedBoard, editorHandler, viewManager } = useSwivuBryntumContext();
  const [generating, setGenerating] = useState(false);
  const slug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;

  const startGenerateForWorkFile = useCallback(
    async (workFileId: number) => {
      if (!slug) {
        showToast({ msg: "No board selected.", type: "error", duration: 3000 });
        return false;
      }
      setGenerating(true);
      try {
        const response = await generateMarketingPage(slug, workFileId);
        const ok =
          response?.status === 202 ||
          response?.status === 200 ||
          (response?.data && typeof response.data.id !== "undefined");
        if (!ok) {
          showToast({
            msg: "Could not start page generation.",
            type: "error",
            duration: 3000,
          });
          return false;
        }
        if (shouldShowAsyncDialog("MARKETING_PAGE_GENERATION")) {
          (editorHandler as { openEditor: (...args: unknown[]) => void }).openEditor(
            EDITOR_KEYS.asyncTaskMessageEditor,
            null,
            { record: "MARKETING_PAGE_GENERATION" },
            { useExisting: true, by: "recordType" },
          );
        }
        // Navigate to the user's saved Pages view so they can see the page once it syncs.
        const pagesView = viewManager?.getDefaultViewByType(VIEW_TYPES.marketingPages);
        if (pagesView) {
          viewManager?.selectView(pagesView);
        }
        return true;
      } catch {
        showToast({
          msg: "Failed to start publishing your marketing page.",
          type: "error",
          duration: 4000,
        });
        return false;
      } finally {
        setGenerating(false);
      }
    },
    [slug, editorHandler, viewManager],
  );

  return { generating, startGenerateForWorkFile };
}
